/**
 * 格式調整器：Pipeline 執行引擎
 *
 * === 問題背景 ===
 *
 * 過去的 Ruby 版本使用程序式 gsub! 鏈，規則間有隱式順序依賴。
 * 調換任兩條規則的程式碼順序就可能產生不同結果，
 * 導致新增或修改規則時非常脆弱且難以測試。
 *
 * === Phase 解決方案 ===
 *
 * 規則被分配到不同 phase（數字越小越先執行）。
 * 同一 phase 內的規則彼此獨立、互不影響。
 * 跨 phase 的執行順序由 phase 數字保證。
 *
 * 執行流程：
 * 1. 收集所有已啟用的規則
 * 2. 依 phase 數字排序（同 phase 保留陣列定義順序，即 stable sort）
 * 3. 依序執行每條規則的 apply() 函式
 *
 * === 維護指南 ===
 *
 * 新增規則時：
 *   判斷它應屬於哪個 phase（看它的輸入是否依賴其他 phase 的輸出），
 *   設定正確的 phase 數字即可。
 *
 * 同 phase 內的規則：
 *   若真的有順序需求，靠在 rules.js 陣列中的定義位置控制。
 *   pipeline 對同 phase 規則保留陣列順序（stable sort）。
 *
 * 新增 phase：
 *   如果新規則不屬於任何現有 phase，可以在現有 phase 之間插入數字。
 *   例如需要在 Phase 4 和 Phase 5 之間插入，可用 Phase 4.5 或重新編號。
 */

import { rules } from './rules'

/**
 * 執行格式化 pipeline。
 *
 * 依據 enabledRuleIds 過濾啟用的規則，按 phase 排序後依序執行。
 *
 * @param {string} text - 待格式化的文字
 * @param {Set<string>|Array<string>} enabledRuleIds - 啟用的規則 ID 集合
 * @returns {string} 格式化後的文字
 */
export function formatText(text, enabledRuleIds) {
  if (!text) return text

  const enabledSet = enabledRuleIds instanceof Set
    ? enabledRuleIds
    : new Set(enabledRuleIds)

  // 過濾已啟用的規則（含 alwaysEnabled），保留陣列原始順序
  const enabledRules = rules.filter(r => enabledSet.has(r.id) || r.alwaysEnabled)

  // 依 phase 排序（stable sort 保證同 phase 內的陣列順序不變）
  const sorted = [...enabledRules].sort((a, b) => a.phase - b.phase)

  let result = text
  for (const rule of sorted) {
    result = rule.apply(result)
  }
  return result
}

/**
 * 取得預設啟用的規則 ID 集合。
 *
 * @returns {Set<string>} 預設啟用的規則 ID
 */
export function getDefaultEnabledIds() {
  return new Set(rules.filter(r => r.enabledByDefault).map(r => r.id))
}
